import { Zap } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { SkeletonCard } from '../../../components/animations/Skeleton';
import { HStack, ProgressBar, StatusBanner, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useAgentDisplayName } from '../hooks/useAgentDisplayName';
import type { PlanUsage, PlanUsageLevel } from '../types';

export interface UsageBreakdownRow {
  agentType: string;
  executions: number;
}

interface UsageSummaryProps {
  planUsage?: PlanUsage;
  isPlanUsageUnavailable: boolean;
  breakdown?: UsageBreakdownRow[];
  isLoading: boolean;
  onUpgradePress?: () => void;
}

const LEVEL_COLORS: Record<PlanUsageLevel, string> = {
  ok: colors.status.success.main,
  warning: colors.status.warning.main,
  critical: colors.status.error.main,
};

function displayPercent(percentUsed: number): number {
  return Math.floor(Math.min(100, Math.max(0, percentUsed)));
}

function BreakdownRow({
  label,
  executions,
  runsLabel,
}: Readonly<{ label: string; executions: number; runsLabel: string }>) {
  return (
    <HStack spacing="sm" align="center" justify="between">
      <Text variant="caption" color={colors.text.secondary} numberOfLines={1} style={{ flex: 1 }}>
        {label}
      </Text>
      <Text variant="caption" color={colors.text.tertiary}>
        {executions} {runsLabel}
      </Text>
    </HStack>
  );
}

function PlanUsageBanner({
  planUsage,
  onUpgradePress,
}: Readonly<{ planUsage: PlanUsage; onUpgradePress?: () => void }>) {
  const { t } = useTranslation();
  const percent = displayPercent(planUsage.percentUsed);
  const critical = planUsage.level === 'critical';
  const upgradeLabel = t('agents.usage.upgradeCta');

  return (
    <StatusBanner
      multiline
      variant={critical ? 'danger' : 'warning'}
      message={t(critical ? 'agents.usage.criticalBanner' : 'agents.usage.warningBanner', {
        percent,
      })}
      trailing={
        onUpgradePress ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={upgradeLabel}
            onPress={onUpgradePress}
            style={styles.ctaTarget}
          >
            <Text variant="caption" color={colors.text.primary} style={styles.cta}>
              {upgradeLabel}
            </Text>
          </Pressable>
        ) : undefined
      }
    />
  );
}

export function UsageSummary({
  planUsage,
  isPlanUsageUnavailable,
  breakdown = [],
  isLoading,
  onUpgradePress,
}: Readonly<UsageSummaryProps>) {
  const { t } = useTranslation();
  const agentDisplayName = useAgentDisplayName();

  if (isLoading) {
    return <SkeletonCard style={styles.skeleton} />;
  }

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <HStack spacing="sm" align="center">
          <Zap size={16} color={colors.accent.purple[400]} strokeWidth={2} />
          <Text variant="h5" color={colors.text.primary} style={styles.title}>
            {t('agents.usage.title')}
          </Text>
        </HStack>
      </View>

      <View style={styles.body}>
        <VStack spacing="md">
          {planUsage ? (
            <VStack spacing="xs">
              <ProgressBar
                value={planUsage.percentUsed}
                color={LEVEL_COLORS[planUsage.level]}
                animated
                height={8}
              />
              <Text variant="caption" color={colors.text.secondary}>
                {t('agents.usage.percentUsed', { percent: displayPercent(planUsage.percentUsed) })}
              </Text>
            </VStack>
          ) : null}

          {planUsage && planUsage.level !== 'ok' ? (
            <PlanUsageBanner planUsage={planUsage} onUpgradePress={onUpgradePress} />
          ) : null}

          {!planUsage && isPlanUsageUnavailable ? (
            <Text variant="caption" color={colors.text.tertiary}>
              {t('agents.usage.unavailable')}
            </Text>
          ) : null}

          {breakdown.length > 0 && (
            <VStack spacing="sm">
              <View style={styles.divider} />
              {breakdown.map((item) => (
                <BreakdownRow
                  key={item.agentType}
                  label={agentDisplayName(item.agentType)}
                  executions={item.executions}
                  runsLabel={t('agents.usage.runs')}
                />
              ))}
            </VStack>
          )}
        </VStack>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  skeleton: {
    minHeight: 180,
  },
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
  title: {
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  body: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border.subtle,
  },
  ctaTarget: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.xs,
  },
  cta: {
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
});
